// Analytics service for Google Analytics event tracking
// This service provides a centralized way to track user interactions and behaviors

const isDevelopment = process.env.NODE_ENV === 'development';

// Helper function to safely call gtag
const gtag = (...args) => {
  if (window.gtag) {
    window.gtag(...args);
  } else if (isDevelopment) {
    console.log('[Analytics]', ...args);
  }
};

// Helper function to get common event parameters
const getCommonParams = () => ({
  timestamp: new Date().toISOString(),
  user_agent: navigator.userAgent,
  screen_resolution: `${window.screen.width}x${window.screen.height}`,
  viewport_size: `${window.innerWidth}x${window.innerHeight}`,
  device_type: window.innerWidth <= 768 ? 'mobile' : 'desktop'
});

// Analytics event tracking functions
const Analytics = {
  // 1. Location Search & Selection Events
  trackLocationSearched: (searchQuery, selectedResult, coordinates) => {
    gtag('event', 'location_searched', {
      event_category: 'location',
      event_label: searchQuery,
      custom_parameters: {
        ...getCommonParams(),
        search_query: searchQuery,
        selected_result: selectedResult,
        result_lat: coordinates?.lat,
        result_lng: coordinates?.lng
      }
    });
  },

  trackMapAreaSelected: (bbox, areaSqm) => {
    gtag('event', 'map_area_selected', {
      event_category: 'location',
      event_label: `${areaSqm}sqm`,
      value: Math.round(areaSqm),
      custom_parameters: {
        ...getCommonParams(),
        bbox_west: bbox[0],
        bbox_south: bbox[1],
        bbox_east: bbox[2],
        bbox_north: bbox[3],
        area_sqm: areaSqm,
        center_lat: (bbox[1] + bbox[3]) / 2,
        center_lng: (bbox[0] + bbox[2]) / 2
      }
    });
  },

  trackMapInteraction: (action, details = {}) => {
    gtag('event', 'map_interaction', {
      event_category: 'map',
      event_action: action,
      custom_parameters: {
        ...getCommonParams(),
        ...details
      }
    });
  },

  // 2. Satellite Image Discovery Events
  trackImagesRequested: (bbox, year, location) => {
    gtag('event', 'images_requested', {
      event_category: 'imagery',
      event_label: year || 'all_years',
      custom_parameters: {
        ...getCommonParams(),
        bbox_west: bbox[0],
        bbox_south: bbox[1],
        bbox_east: bbox[2],
        bbox_north: bbox[3],
        year: year,
        center_lat: (bbox[1] + bbox[3]) / 2,
        center_lng: (bbox[0] + bbox[2]) / 2,
        location_name: location
      }
    });
  },

  trackImagesLoaded: (imageCount, cloudDistribution, year) => {
    gtag('event', 'images_loaded', {
      event_category: 'imagery',
      event_label: `${imageCount} images`,
      value: imageCount,
      custom_parameters: {
        ...getCommonParams(),
        image_count: imageCount,
        year: year,
        avg_cloud_coverage: cloudDistribution?.avg,
        min_cloud_coverage: cloudDistribution?.min,
        max_cloud_coverage: cloudDistribution?.max
      }
    });
  },

  trackCloudFilterApplied: (filterType, location) => {
    gtag('event', 'cloud_filter_applied', {
      event_category: 'imagery',
      event_action: 'filter',
      event_label: filterType,
      custom_parameters: {
        ...getCommonParams(),
        filter_type: filterType,
        location: location
      }
    });
  },

  trackImageSelected: (imageDate, cloudPercentage, location, selectionMethod) => {
    gtag('event', 'image_selected', {
      event_category: 'imagery',
      event_action: 'select',
      event_label: imageDate,
      custom_parameters: {
        ...getCommonParams(),
        image_date: imageDate,
        cloud_percentage: cloudPercentage,
        location: location,
        selection_method: selectionMethod // 'thumbnail_click', 'timeline_nav', etc.
      }
    });
  },

  // 3. Super-Resolution (Magic Zoom) Events
  trackMagicZoomInitiated: (bbox, date, areaSqm, cloudCoverage) => {
    gtag('event', 'magic_zoom_initiated', {
      event_category: 'engagement',
      event_action: 'super_resolution',
      event_label: date,
      value: 1,
      custom_parameters: {
        ...getCommonParams(),
        bbox_west: bbox[0],
        bbox_south: bbox[1],
        bbox_east: bbox[2],
        bbox_north: bbox[3],
        area_sqm: areaSqm,
        image_date: date,
        cloud_coverage: cloudCoverage,
        center_lat: (bbox[1] + bbox[3]) / 2,
        center_lng: (bbox[0] + bbox[2]) / 2
      }
    });
  },

  trackMagicZoomCompleted: (success, processingTime, queueWaitTime, location) => {
    gtag('event', 'magic_zoom_completed', {
      event_category: 'engagement',
      event_action: 'super_resolution',
      event_label: success ? 'success' : 'failure',
      value: Math.round(processingTime),
      custom_parameters: {
        ...getCommonParams(),
        success: success,
        processing_time_ms: processingTime,
        queue_wait_time_ms: queueWaitTime,
        total_time_ms: processingTime + queueWaitTime,
        location: location
      }
    });
  },

  trackMagicZoomCancelled: (progressStage, timeElapsed) => {
    gtag('event', 'magic_zoom_cancelled', {
      event_category: 'engagement',
      event_action: 'super_resolution',
      event_label: progressStage,
      custom_parameters: {
        ...getCommonParams(),
        progress_stage: progressStage,
        time_elapsed_ms: timeElapsed
      }
    });
  },

  trackQueuePosition: (position, queueLength) => {
    gtag('event', 'queue_position', {
      event_category: 'performance',
      event_action: 'queue',
      event_label: `position_${position}`,
      value: position,
      custom_parameters: {
        ...getCommonParams(),
        queue_position: position,
        queue_length: queueLength
      }
    });
  },

  // 4. Advanced Features Events
  trackViewModeChanged: (newMode, previousMode, context) => {
    gtag('event', 'view_mode_changed', {
      event_category: 'features',
      event_action: 'view_mode',
      event_label: newMode,
      custom_parameters: {
        ...getCommonParams(),
        new_mode: newMode,
        previous_mode: previousMode,
        context: context // 'image_list', 'map_view'
      }
    });
  },

  trackGeotiffDownloaded: (location, date, fileSize, isSupperResolution) => {
    gtag('event', 'geotiff_downloaded', {
      event_category: 'features',
      event_action: 'download',
      event_label: isSupperResolution ? 'super_resolution' : 'regular',
      value: Math.round(fileSize / 1024), // KB
      custom_parameters: {
        ...getCommonParams(),
        location: location,
        image_date: date,
        file_size_kb: Math.round(fileSize / 1024),
        is_super_resolution: isSupperResolution
      }
    });
  },

  trackImageComparison: (action, imageDate) => {
    gtag('event', 'image_comparison', {
      event_category: 'features',
      event_action: 'compare',
      event_label: action,
      custom_parameters: {
        ...getCommonParams(),
        comparison_action: action,
        image_date: imageDate
      }
    });
  },

  // 5. Session & Engagement Events
  trackSessionStarted: (entryPoint, referrer) => {
    gtag('event', 'session_started', {
      event_category: 'session',
      event_action: 'start',
      event_label: entryPoint,
      custom_parameters: {
        ...getCommonParams(),
        entry_point: entryPoint,
        referrer: referrer || 'direct',
        url_hash: window.location.hash
      }
    });
  },

  trackFirstVisitPopup: (action) => {
    gtag('event', 'first_visit_popup', {
      event_category: 'onboarding',
      event_action: action, // 'shown', 'dismissed', 'read_complete'
      event_label: 'first_visit',
      custom_parameters: {
        ...getCommonParams(),
        action: action
      }
    });
  },

  trackTimeOnLocation: (location, duration) => {
    gtag('event', 'time_on_location', {
      event_category: 'engagement',
      event_action: 'view_duration',
      event_label: location,
      value: Math.round(duration / 1000), // seconds
      custom_parameters: {
        ...getCommonParams(),
        location: location,
        duration_seconds: Math.round(duration / 1000)
      }
    });
  },

  trackImagesPerSession: (count) => {
    gtag('event', 'images_per_session', {
      event_category: 'engagement',
      event_action: 'session_activity',
      event_label: 'images_viewed',
      value: count,
      custom_parameters: {
        ...getCommonParams(),
        images_viewed: count
      }
    });
  },

  // 6. Performance & Error Events
  trackApiError: (endpoint, errorType, context) => {
    gtag('event', 'api_error', {
      event_category: 'errors',
      event_action: endpoint,
      event_label: errorType,
      custom_parameters: {
        ...getCommonParams(),
        endpoint: endpoint,
        error_type: errorType,
        context: context
      }
    });
  },

  trackSlowLoad: (resourceType, loadTime, url) => {
    gtag('event', 'slow_load', {
      event_category: 'performance',
      event_action: 'slow_resource',
      event_label: resourceType,
      value: Math.round(loadTime),
      custom_parameters: {
        ...getCommonParams(),
        resource_type: resourceType,
        load_time_ms: loadTime,
        resource_url: url
      }
    });
  },

  trackFeatureDiscovery: (feature, discoveryMethod) => {
    gtag('event', 'feature_discovery', {
      event_category: 'engagement',
      event_action: 'discover',
      event_label: feature,
      custom_parameters: {
        ...getCommonParams(),
        feature: feature,
        discovery_method: discoveryMethod
      }
    });
  },

  // 7. Geographic Analytics
  trackPopularLocations: (locations) => {
    // This would typically be done server-side, but we can track individual views
    gtag('event', 'location_view', {
      event_category: 'geography',
      event_action: 'view',
      custom_parameters: {
        ...getCommonParams(),
        ...locations
      }
    });
  },

  trackLocationRevisit: (location, visitCount) => {
    gtag('event', 'location_revisit', {
      event_category: 'geography',
      event_action: 'revisit',
      event_label: location,
      value: visitCount,
      custom_parameters: {
        ...getCommonParams(),
        location: location,
        visit_count: visitCount
      }
    });
  }
};

export default Analytics;